import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Sparkles, Lock, Check, ChevronLeft, ChevronDown, ChevronRight, RotateCcw, Zap } from "lucide-react";
import { GoldCoin } from "@/components/CoinIcons";
import api, { apiErr } from "@/lib/api";
import { PageHeader, Loader, btn } from "@/components/Page";
import Modal from "@/components/Modal";
import { skillTreeGladiatorId, skillTreeRespecUrl } from "@/utils/skillTree";

const BRANCH_ACCENT = ["text-red-400 border-red-700/40", "text-sky-400 border-sky-700/40", "text-amber-400 border-amber-700/40"];

export default function SkillTreeManager() {
  const navigate = useNavigate();
  const [gladiators, setGladiators] = useState(null);
  const [expandedGlad, setExpandedGlad] = useState(null);
  const [skillTreeData, setSkillTreeData] = useState({});
  const [busy, setBusy] = useState(false);
  const [respecOpen, setRespecOpen] = useState(false);
  const [respecGlad, setRespecGlad] = useState(null);

  const loadGladiators = async () => {
    try {
      const { data } = await api.get("/gladiators");
      // Filter gladiators with skill points > 0
      const withPoints = (data.active || [])
        .filter(g => g.skill_points > 0)
        .sort((a, b) => b.skill_points - a.skill_points);
      
      // For each gladiator with points, check if they can actually unlock any skills
      const gladiatorsWithUnlockableSkills = [];
      
      for (const glad of withPoints) {
        try {
          const { data: treeData } = await api.get(`/gladiators/${glad.id}/skill-tree`);
          // Check if any node in any branch can be unlocked
          const hasUnlockableSkills = treeData.branches.some(branch => 
            branch.nodes.some(node => node.can_unlock === true)
          );
          
          if (hasUnlockableSkills) {
            gladiatorsWithUnlockableSkills.push(glad);
            // Pre-cache the skill tree data
            setSkillTreeData(prev => ({ ...prev, [glad.id]: treeData }));
          }
        } catch (e) {
          // If we can't load skill tree, skip this gladiator
          console.error(`Failed to load skill tree for ${glad.name}:`, e);
        }
      }
      
      setGladiators(gladiatorsWithUnlockableSkills);
    } catch (e) {
      toast.error(apiErr(e));
      setGladiators([]);
    }
  };

  const loadSkillTree = async (gladId) => {
    // Check if already loaded (from initial load or previous expansion)
    if (skillTreeData[gladId]) return;
    try {
      const { data } = await api.get(`/gladiators/${gladId}/skill-tree`);
      setSkillTreeData(prev => ({ ...prev, [gladId]: data }));
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  useEffect(() => {
    loadGladiators();
  }, []);

  const toggleExpand = (glad) => {
    if (expandedGlad === glad.id) {
      // User is manually collapsing - allow re-sort
      setExpandedGlad(null);
      // Re-sort the list now that user is done with this gladiator
      setGladiators(prev => [...prev].sort((a, b) => b.skill_points - a.skill_points));
    } else {
      setExpandedGlad(glad.id);
      loadSkillTree(glad.id);
    }
  };

  const unlock = async (gladId, nodeId) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/gladiators/${gladId}/skill-tree/unlock`, { node_id: nodeId });
      setSkillTreeData(prev => ({ ...prev, [gladId]: data }));
      
      // Update gladiator skill points in list WITHOUT re-sorting (keep expanded glad in place)
      setGladiators(prev => {
        const updated = prev.map(g => g.id === gladId ? { ...g, skill_points: data.skill_points } : g);
        // Only remove if 0 skill points, but DON'T sort yet
        return updated.filter(g => g.skill_points > 0);
      });
      
      toast.success("Technique unlocked");
      
      // If gladiator now has 0 skill points, collapse and remove
      if (data.skill_points === 0) {
        setExpandedGlad(null);
        // Re-sort after removing
        setGladiators(prev => [...prev].sort((a, b) => b.skill_points - a.skill_points));
      }
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setBusy(false);
    }
  };

  const respec = async () => {
    if (!respecGlad) return;
    const gladiatorId = skillTreeGladiatorId(respecGlad);
    const respecUrl = skillTreeRespecUrl(respecGlad);
    if (!gladiatorId) {
      toast.error("Unable to identify this gladiator. Please reopen the skill tree.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(respecUrl);
      setSkillTreeData(prev => ({ ...prev, [gladiatorId]: data }));
      
      // Update gladiator in list WITHOUT re-sorting (keep expanded glad in place)
      setGladiators(prev => 
        prev.map(g => g.id === gladiatorId ? { ...g, skill_points: data.skill_points } : g)
      );
      
      setRespecOpen(false);
      setRespecGlad(null);
      toast.success(data.gold_spent === 0
        ? `Free respec complete — ${data.refunded} skill points refunded`
        : `Respec complete — ${data.refunded} skill points refunded (${data.gold_spent} gold spent)`);
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setBusy(false);
    }
  };

  if (gladiators === null) return <Loader label="Loading skill trees…" />;

  const totalAvailablePoints = gladiators.reduce((sum, g) => sum + g.skill_points, 0);

  return (
    <div>
      <button
        data-testid="skilltree-manager-back-btn"
        onClick={() => navigate("/roster")}
        className="flex items-center gap-1 text-zinc-500 hover:text-amber-300 text-sm mb-3 transition-colors"
      >
        <ChevronLeft size={16} /> Back to Roster
      </button>
      
      <PageHeader
        title="Skill Tree Manager"
        subtitle={`Upgrade techniques for all gladiators in one place`}
        action={
          <div className="flex items-center gap-2 px-4 py-2 border border-amber-600/50 bg-amber-950/30">
            <Zap size={16} className="text-amber-400" />
            <span className="font-cinzel text-amber-300 text-lg tabular-nums">{totalAvailablePoints}</span>
            <span className="text-[10px] uppercase tracking-widest text-zinc-500">Total Points</span>
          </div>
        }
      />

      {gladiators.length === 0 ? (
        <div className="border border-zinc-800 bg-zinc-900/40 p-8 text-center">
          <p className="text-zinc-400 mb-2">No gladiators with unlockable skills</p>
          <p className="text-sm text-zinc-500">Gladiators need available skill points and unlockable skill nodes to appear here</p>
        </div>
      ) : (
        <div className="space-y-3">
          {gladiators.map(glad => {
            const isExpanded = expandedGlad === glad.id;
            const treeData = skillTreeData[glad.id];
            
            return (
              <div key={glad.id} className="border border-zinc-800 bg-zinc-900/60">
                {/* Accordion Header */}
                <button
                  data-testid={`gladiator-accordion-${glad.id}`}
                  onClick={() => toggleExpand(glad)}
                  className="w-full flex items-center justify-between gap-4 p-4 hover:bg-zinc-800/40 transition-colors text-left"
                >
                  <div className="flex items-center gap-3">
                    {isExpanded ? <ChevronDown size={20} className="text-zinc-500" /> : <ChevronRight size={20} className="text-zinc-500" />}
                    <div>
                      <div className="font-cinzel text-amber-200">{glad.name}</div>
                      <div className="text-xs text-zinc-500">{glad.cls}</div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2 px-3 py-1.5 border border-amber-700/50 bg-amber-950/20 rounded-sm">
                    <Zap size={14} className="text-amber-400" />
                    <span className="font-cinzel text-amber-300 tabular-nums">{glad.skill_points}</span>
                    <span className="text-[9px] uppercase tracking-widest text-zinc-600">pts</span>
                  </div>
                </button>

                {/* Expanded Skill Tree Content */}
                {isExpanded && (
                  <div className="border-t border-zinc-800 p-6 bg-zinc-950/80">
                    {!treeData ? (
                      <div className="text-center py-8">
                        <Loader label="Loading skill tree…" />
                      </div>
                    ) : (
                      <>
                        <div className="flex items-center justify-between mb-4">
                          <div>
                            <p className="text-sm text-zinc-400">{treeData.cls} · {treeData.title} discipline</p>
                          </div>
                          <div className="flex items-center gap-3">
                            <div className="flex items-center gap-2 px-3 py-2 border border-amber-600/50 bg-amber-950/30">
                              <Zap size={14} className="text-amber-400" />
                              <span className="font-cinzel text-amber-300 tabular-nums">{treeData.skill_points}</span>
                              <span className="text-[9px] uppercase tracking-widest text-zinc-500">available</span>
                            </div>
                            {treeData.unlocked_count > 0 && (() => {
                              const respecCost = treeData.respec_cost ?? 50;
                              const freeRespecAvailable = Boolean(treeData.free_respec_available);
                              const canAffordRespec = freeRespecAvailable || (treeData.gold ?? 0) >= respecCost;
                              return (
                              <button
                                data-testid={`respec-btn-${glad.id}`}
                                disabled={busy || !canAffordRespec}
                                onClick={() => { setRespecGlad({ ...treeData, gladiator_id: treeData.gladiator_id || glad.id }); setRespecOpen(true); }}
                                title={freeRespecAvailable ? "One free respec available" : `Costs ${respecCost} gold — you have ${treeData.gold ?? 0}`}
                                className={btn("ghost") + " py-2 px-3 flex items-center gap-1.5 text-xs"}
                              >
                                <RotateCcw size={12} /> Respec
                                <span data-testid={`respec-cost-label-${glad.id}`} className={`text-[9px] ${freeRespecAvailable ? "text-emerald-400" : !canAffordRespec ? "text-red-400" : "text-zinc-500"}`}>
                                  · {freeRespecAvailable ? "Free" : `${respecCost} gold`}
                                </span>
                              </button>
                              );
                            })()}
                          </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                          {treeData.branches.map((br, bi) => (
                            <div key={br.id} data-testid={`branch-${br.id}`} className={`border ${BRANCH_ACCENT[bi % 3].split(" ")[1]} bg-zinc-950/60 p-4`}>
                              <h3 className={`font-cinzel uppercase tracking-widest text-xs mb-3 ${BRANCH_ACCENT[bi % 3].split(" ")[0]}`}>
                                {br.name}
                              </h3>
                              <div className="space-y-2">
                                {br.nodes.map((node, ni) => (
                                  <div key={node.id}>
                                    {ni > 0 && <div className="mx-auto w-px h-2 bg-zinc-700" />}
                                    <NodeCard
                                      node={node}
                                      busy={busy}
                                      onUnlock={() => unlock(glad.id, node.id)}
                                    />
                                  </div>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Respec Modal */}
      <Modal open={respecOpen} onClose={() => { setRespecOpen(false); setRespecGlad(null); }} title="Respec Techniques">
        {respecGlad && (
          <div data-testid="respec-modal" className="space-y-4">
            <p className="text-sm text-zinc-300 leading-relaxed">
              Reset <span className="text-amber-300 font-medium">all unlocked techniques</span> for{" "}
              <span className="text-amber-300 font-medium">{respecGlad.name}</span> and refund every skill point spent?
            </p>
            <div data-testid="respec-cost-summary" className="flex items-center gap-2 border border-amber-900/40 bg-amber-950/20 px-4 py-3 rounded-sm">
              <GoldCoin size={16} className="text-amber-400" />
              <span className="text-sm text-zinc-300">
                {respecGlad.free_respec_available ? <span className="font-cinzel text-emerald-300">Your one free respec is available</span> : <>This costs <span className="font-cinzel text-amber-300">{respecGlad.respec_cost ?? 50} gold</span></>}
              </span>
              {!respecGlad.free_respec_available && <span className="ml-auto text-[11px] uppercase tracking-widest text-zinc-500">You have {respecGlad.gold ?? 0}</span>}
            </div>
            <div className="flex gap-2 justify-end pt-1">
              <button
                data-testid="respec-cancel-btn"
                onClick={() => { setRespecOpen(false); setRespecGlad(null); }}
                disabled={busy}
                className="px-4 py-2 text-sm border border-zinc-700 text-zinc-300 hover:border-zinc-500 rounded-sm transition-colors"
              >
                Cancel
              </button>
              <button
                data-testid="respec-confirm-btn"
                onClick={respec}
                disabled={busy}
                className="px-4 py-2 text-sm font-cinzel uppercase tracking-wide flex items-center gap-1.5 bg-amber-500 text-zinc-950 hover:bg-amber-400 rounded-sm transition-colors disabled:opacity-50"
              >
                <RotateCcw size={14} /> {busy ? "Resetting…" : "Confirm Respec"}
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

function NodeCard({ node, busy, onUnlock }) {
  const state = node.unlocked ? "unlocked" : node.can_unlock ? "available" : "locked";
  const styles = {
    unlocked: "border-emerald-600/50 bg-emerald-950/20",
    available: "border-amber-600/50 bg-amber-950/15",
    locked: "border-zinc-800 bg-zinc-900/40 opacity-70"
  };

  return (
    <div data-testid={`node-${node.id}`} className={`border ${styles[state]} p-3 transition-all`}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-1.5">
          <span className="text-[9px] uppercase tracking-widest text-zinc-600">T{node.tier}</span>
          <span className="font-cinzel text-sm text-zinc-100">{node.name}</span>
        </div>
        {node.unlocked
          ? <Check size={15} className="text-emerald-400 shrink-0" />
          : state === "locked" ? <Lock size={13} className="text-zinc-600 shrink-0" /> : null}
      </div>
      <p className="text-xs text-zinc-400 mt-1.5 leading-snug">{node.desc}</p>
      <div className="flex items-center justify-between mt-2.5">
        <span className="text-[10px] uppercase tracking-widest text-amber-500/80">{node.cost} pts</span>
        {node.unlocked ? (
          <span className="text-[10px] uppercase tracking-widest text-emerald-400">Unlocked</span>
        ) : node.can_unlock ? (
          <button
            data-testid={`unlock-${node.id}`}
            disabled={busy}
            onClick={onUnlock}
            className="text-[11px] uppercase tracking-widest text-amber-300 border border-amber-700/50 px-2.5 py-1 hover:bg-amber-900/40 transition-all disabled:opacity-40"
          >
            Unlock
          </button>
        ) : (
          <span className="max-w-[12rem] text-right text-[10px] leading-relaxed text-zinc-300">{node.locked_reason}</span>
        )}
      </div>
    </div>
  );
}
